import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, FileText, Landmark, Scale, Loader2, AlertTriangle, Info, X,
  Download, Share2, Eye, ShieldAlert, CheckCircle2,
} from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const REPORT_STATUS = {
  DRAFT: { label: "Bozza", cls: "bg-slate-100 text-slate-700" },
  IN_REVIEW: { label: "In revisione", cls: "bg-amber-100 text-amber-800" },
  APPROVED: { label: "Approvato", cls: "bg-green-100 text-green-800" },
};
export const TEMPLATE_ICONS = {
  VSME_BASIC: FileText, VSME_COMPREHENSIVE: FileText,
  MEF_PMI_BANCHE: Landmark, ESG_PMI_SCORE: Scale,
};
const SEVERITY_STYLE = {
  BLOCKING: { cls: "bg-red-100 text-red-800 border-red-300", Icon: ShieldAlert },
  WARNING: { cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: AlertTriangle },
  INFO: { cls: "bg-sky-100 text-sky-800 border-sky-300", Icon: Info },
};
const JOB_ACTIVE = ["QUEUED", "VALIDATING", "SNAPSHOTTING", "RENDERING", "STORING"];
const JOB_PHASES = {
  QUEUED: "In coda…",
  VALIDATING: "Validazione dei dati…",
  SNAPSHOTTING: "Creazione dello snapshot dei dati…",
  RENDERING: "Composizione del documento e della narrativa…",
  STORING: "Salvataggio del documento…",
};

export default function Report() {
  const { companyId, period, can } = useTenant();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState(null);
  const [reports, setReports] = useState(null);
  const [wizard, setWizard] = useState(null); // {template, validation}
  const [busy, setBusy] = useState(null);
  const [job, setJob] = useState(null);
  const canGenerate = can("analysis:report:generate");
  const jobActive = !!job && JOB_ACTIVE.includes(job.status);
  const jobRef = useRef(null);
  jobRef.current = job;

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const reqs = [
        api.get(`/companies/${companyId}/periods/${period.id}/reports/templates`),
        api.get(`/companies/${companyId}/periods/${period.id}/reports`),
      ];
      if (canGenerate) {
        reqs.push(api.get(`/companies/${companyId}/periods/${period.id}/reports/jobs/active`)
          .catch(() => ({ data: { job: null } })));
      }
      const [t, r, j] = await Promise.all(reqs);
      setTemplates(t.data.templates);
      setReports(r.data.reports);
      if (j?.data?.job && !jobRef.current) setJob(j.data.job);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [companyId, period?.id, canGenerate]);

  useEffect(() => { load(); }, [load]);

  // Polling controllato dello stato del job: nessuna richiesta tenuta aperta.
  useEffect(() => {
    if (!jobActive) return undefined;
    const timer = setInterval(async () => {
      const current = jobRef.current;
      if (!current?.status_url) return;
      try {
        const { data } = await api.get(current.status_url);
        setJob(data);
        if (data.status === "COMPLETED") {
          toast.success("Report generato: la bozza è pronta.");
          load();
          navigate(`/report/${data.report_id}`);
        } else if (data.status === "FAILED" || data.status === "CANCELLED") {
          if (data.status === "FAILED") {
            toast.error(data.error?.message || "Il report non è stato generato.");
          }
          load();
        }
      } catch { /* connessione persa: si riprova al prossimo tick */ }
    }, 2500);
    return () => clearInterval(timer);
  }, [jobActive, load, navigate]);

  const openWizard = async (tpl) => {
    setBusy(`pre-${tpl.code}`);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/reports/pre-validate`,
        { template_code: tpl.code });
      setWizard({ template: tpl, validation: data });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const generate = async () => {
    if (!wizard || jobActive) return;
    setBusy("generate");
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/reports`,
        { template_code: wizard.template.code });
      setWizard(null);
      if (data.status === "COMPLETED" && data.report_id) {
        toast.success("Esiste già una bozza generata con questi dati: la apro.");
        load();
        navigate(`/report/${data.report_id}`);
        return;
      }
      setJob(data);
      toast.success("Generazione report avviata: continua in background, puoi lasciare questa pagina.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const retryJob = async () => {
    if (!job) return;
    setBusy("retry");
    try {
      const { data } = await api.post(`${job.status_url}/retry`);
      setJob(data);
      toast.success("Generazione riavviata.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const download = async (report) => {
    try {
      const r = await api.get(
        `/companies/${companyId}/periods/${period.id}/reports/${report.id}/download`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.template_code}_${period.year}_v${report.version_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="report-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Report ESG</h1>
        <p className="text-sm text-slate-600 mt-1">
          Genera report professionali VSME, MEF PMI-Banche ed ESG-PMI dai dati verificati.
          La generazione avviene in background: il report è congelato su uno snapshot e,
          dopo l'approvazione, diventa immutabile.
        </p>
      </div>

      {job && (
        <Card className={`border ${job.status === "FAILED" ? "border-red-300 bg-red-50"
          : job.status === "COMPLETED" ? "border-green-300 bg-green-50"
          : "border-sky-300 bg-sky-50"}`} data-testid="report-job-card">
          <CardContent className="p-4 flex items-center gap-3">
            {jobActive ? (
              <Loader2 className="w-5 h-5 animate-spin text-sky-700 shrink-0" aria-hidden="true" />
            ) : job.status === "COMPLETED" ? (
              <CheckCircle2 className="w-5 h-5 text-green-700 shrink-0" aria-hidden="true" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-700 shrink-0" aria-hidden="true" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900" data-testid="report-job-status">
                {jobActive ? "Generazione report in corso"
                  : job.status === "COMPLETED" ? "Generazione completata"
                  : job.status === "CANCELLED" ? "Generazione annullata"
                  : "Generazione non riuscita"}
              </p>
              <p className="text-xs text-slate-600" data-testid="report-job-phase">
                {jobActive
                  ? `${JOB_PHASES[job.status] || job.status} Puoi lasciare questa pagina: continua in background.`
                  : job.status === "FAILED"
                    ? (job.error?.message || "Il report non è stato generato. Nessun documento definitivo è stato creato.")
                    : job.status === "COMPLETED" ? "La bozza è disponibile nello storico report." : ""}
              </p>
            </div>
            {job.status === "FAILED" && canGenerate && (
              <Button size="sm" className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
                disabled={busy === "retry"} onClick={retryJob} data-testid="report-job-retry-btn">
                {busy === "retry" ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> : "Riprova"}
              </Button>
            )}
            {job.status === "COMPLETED" && job.report_id && (
              <Button size="sm" variant="outline" className="h-8 text-xs"
                onClick={() => navigate(`/report/${job.report_id}`)}
                data-testid="report-job-open-btn">Apri report</Button>
            )}
            {!jobActive && (
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                onClick={() => setJob(null)} data-testid="report-job-dismiss-btn">
                <X className="w-4 h-4" aria-hidden="true" />
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="report-templates">
        {(templates || []).map((tpl) => {
          const Icon = TEMPLATE_ICONS[tpl.code] || BarChart3;
          return (
            <Card key={tpl.code} className="border-slate-200" data-testid={`template-card-${tpl.code}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-green-100 text-green-800">
                    <Icon className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900">{tpl.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{tpl.audience}</p>
                    <p className="text-xs text-slate-600 mt-2 leading-relaxed">{tpl.description}</p>
                  </div>
                </div>
                {canGenerate && (
                  <Button size="sm" className="mt-4 h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
                    disabled={!!busy || jobActive} onClick={() => openWizard(tpl)}
                    data-testid={`generate-btn-${tpl.code}`}>
                    {busy === `pre-${tpl.code}`
                      ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
                      : jobActive && job?.template_code === tpl.code
                        ? "Elaborazione in corso…"
                        : "Genera nuovo report"}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
        {!templates && <p className="text-sm text-slate-500">Caricamento…</p>}
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Storico report — esercizio {period?.year}</CardTitle>
        </CardHeader>
        <CardContent>
          {!reports ? (
            <p className="text-sm text-slate-500">Caricamento…</p>
          ) : reports.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="reports-empty">
              Nessun report generato per questo periodo.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reports-table">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-semibold">Report</th>
                    <th className="py-2 pr-3 font-semibold">Versione</th>
                    <th className="py-2 pr-3 font-semibold">Stato</th>
                    <th className="py-2 pr-3 font-semibold">Visibilità</th>
                    <th className="py-2 pr-3 font-semibold">Creato</th>
                    <th className="py-2 font-semibold text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r) => {
                    const st = REPORT_STATUS[r.status] || REPORT_STATUS.DRAFT;
                    return (
                      <tr key={r.id} className="border-b border-slate-100"
                        data-testid={`report-row-${r.id}`}>
                        <td className="py-2.5 pr-3 font-medium text-slate-900">{r.template_title}</td>
                        <td className="py-2.5 pr-3 text-slate-600">v{r.version_number}</td>
                        <td className="py-2.5 pr-3">
                          <Badge className={st.cls} data-testid={`report-status-${r.id}`}>{st.label}</Badge>
                          {r.new_data_available && r.status === "APPROVED" && (
                            <Badge className="ml-1 bg-amber-100 text-amber-800"
                              data-testid={`report-stale-${r.id}`}>Nuovi dati</Badge>
                          )}
                        </td>
                        <td className="py-2.5 pr-3">
                          {r.visibility === "SHARED_WITH_CLIENT" ? (
                            <Badge className="bg-indigo-100 text-indigo-800"
                              data-testid={`report-shared-${r.id}`}>
                              <Share2 className="w-3 h-3 mr-1" aria-hidden="true" />Condiviso
                            </Badge>
                          ) : (
                            <span className="text-xs text-slate-500">Solo consulente</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-xs text-slate-500">
                          {new Date(r.created_at).toLocaleDateString("it-IT")}
                        </td>
                        <td className="py-2.5 text-right whitespace-nowrap">
                          <Button size="sm" variant="outline" className="h-7 text-xs mr-1"
                            onClick={() => navigate(`/report/${r.id}`)}
                            data-testid={`report-open-${r.id}`}>
                            <Eye className="w-3 h-3 mr-1" aria-hidden="true" />Apri
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs"
                            onClick={() => download(r)} data-testid={`report-download-${r.id}`}>
                            <Download className="w-3 h-3" aria-hidden="true" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {wizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog" aria-modal="true" data-testid="prevalidation-dialog">
          <Card className="w-full max-w-lg border-slate-200 max-h-[85vh] overflow-y-auto">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <CardTitle className="text-base">Pre-validazione — {wizard.template.title}</CardTitle>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                  onClick={() => setWizard(null)} data-testid="prevalidation-close">
                  <X className="w-4 h-4" aria-hidden="true" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {wizard.validation.checks.length === 0 ? (
                <p className="text-sm text-green-800" data-testid="prevalidation-ok">
                  Nessun rilievo: i dati sono pronti per la generazione del report.
                </p>
              ) : (
                <ul className="space-y-2">
                  {wizard.validation.checks.map((c) => {
                    const s = SEVERITY_STYLE[c.severity] || SEVERITY_STYLE.INFO;
                    return (
                      <li key={c.code} className={`flex items-start gap-2 rounded-md border p-2.5 ${s.cls}`}
                        data-testid={`check-item-${c.code}`}>
                        <s.Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                        <div>
                          <p className="text-xs font-semibold">{c.code}</p>
                          <p className="text-xs">{c.message}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" className="h-8 text-xs"
                  onClick={() => setWizard(null)} data-testid="prevalidation-cancel">
                  Annulla
                </Button>
                <Button size="sm" className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
                  disabled={!wizard.validation.can_generate || busy === "generate" || jobActive}
                  onClick={generate} data-testid="confirm-generate-btn">
                  {busy === "generate"
                    ? <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
                    : jobActive ? "Generazione già in corso…" : "Genera report"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
